import { batch, effect } from '@preact/signals';
import type { Card, CardMessage, CodeProposal, MessagePart, NativeTurn, PromptContext, ProviderId, Settings, Usage } from '../types';
import { DEFAULT_TITLE } from '../types';
import * as db from '../storage/db';
import { saveKeys, saveSettings, clearAllLocalData } from '../storage/settings';
import { markLegacyMigrated, readLegacyData } from '../storage/migrate';
import { createProvider, isAbort, providerMeta, type Provider } from '../ai';
import { parseProposedChanges, PROPOSE_CHANGES, proposeChangesTool } from '../ai/tools';
import { buildSentPrompt } from '../ai/convert';
import {
  buildSystemPrompt,
  cleanSuggestions,
  editSelectionMessage,
  PROMPTS,
  SUGGESTIONS_SCHEMA,
  SUGGESTIONS_SYSTEM,
  suggestionsPrompt,
  TITLE_SYSTEM,
  titlePrompt,
  type SuggestionState,
} from '../ai/prompts';
import { normalizeLanguage } from '../languages';
import { newId } from '../util/id';
import { debounce } from '../util/debounce';
import { readPartialStringFields } from '../util/partial-json';
import { specState, syncOf } from '../util/spec';
import { outputText, previewDoc, previewKind, previewSource, resetOutput, runFailed, runCompletions } from '../run/output';
import { previewTitle, previewUndocked } from '../run/preview-window';
import { runCode, stopRun } from '../run/runner';
import type { ReviewState } from '../editor/editor';
import {
  activeCard,
  activeCode,
  activeSpec,
  apiKeys,
  attach,
  cardList,
  currentModel,
  editorBridge,
  isDark,
  legacyImportCount,
  modelListStatus,
  modelOptions,
  review,
  settings,
  showToast,
  specBridge,
  streaming,
  suggestions,
  ui,
  updateUi,
  type DocReview,
} from './store';

const ACTIVE_CARD_KEY = 'cue.activeCard';
/** Written before cards existed; read once so the last open card survives the rename. */
const LEGACY_ACTIVE_KEY = 'cue.activeChat';
const narrow = () => window.innerWidth < 800;

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

function snapshotActive(): Card | null {
  const card = activeCard.value;
  if (!card) return null;
  return { ...card, code: activeCode.value, spec: activeSpec.value };
}

/** Debounced save for high-frequency changes (typing, streaming tokens). */
const persistActive = debounce(() => {
  const card = snapshotActive();
  if (card) void db.putCard(card);
}, 400);

/**
 * Immediate save for turn boundaries (a message added, a title set, a review decided).
 * IndexedDB writes started during unload are not guaranteed to commit, so anything the user would miss
 * after a reload must not wait for the debounce.
 */
function persistNow(): void {
  persistActive.cancel();
  const card = snapshotActive();
  if (card) void db.putCard(card);
}

function touch(card: Card): Card {
  return { ...card, updatedAt: Date.now() };
}

function updateList(card: Card): void {
  const summary = db.toSummary(card);
  cardList.value = [summary, ...cardList.value.filter((c) => c.id !== card.id)].sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Apply a change to the active card (if it is the target) or directly to storage (if the user moved on). */
async function mutateCard(cardId: string, fn: (card: Card) => Card): Promise<void> {
  const current = activeCard.value;
  if (current && current.id === cardId) {
    const next = touch(fn(snapshotActive() ?? current));
    activeCard.value = next;
    updateList(next);
    persistActive();
    return;
  }
  const stored = await db.getCard(cardId);
  if (!stored) return;
  const next = touch(fn(stored));
  await db.putCard(next);
  updateList(next);
}

function mutateActive(fn: (card: Card) => Card): void {
  const current = snapshotActive();
  if (!current) return;
  const next = touch(fn(current));
  activeCard.value = next;
  updateList(next);
  persistNow();
}

// ---------------------------------------------------------------------------
// Startup
// ---------------------------------------------------------------------------

function applyTheme(): void {
  const theme = settings.value.theme;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && prefersDark);
  isDark.value = dark;
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

async function importLegacyIfNeeded(): Promise<void> {
  const legacy = readLegacyData();
  if (!legacy) return;
  if (legacy.cards.length > 0) {
    await db.importCards(legacy.cards);
    legacyImportCount.value = legacy.cards.length;
  }
  const patch: Partial<Settings> = {};
  if (legacy.provider) patch.provider = legacy.provider;
  if (legacy.systemPrompt) patch.systemPrompt = legacy.systemPrompt;
  if (legacy.provider && legacy.model) patch.models = { ...settings.value.models, [legacy.provider]: legacy.model };
  if (Object.keys(patch).length > 0) updateSettings(patch);
  if (legacy.provider && legacy.apiKey) setApiKey(legacy.provider, legacy.apiKey);
  markLegacyMigrated();
}

function readActiveId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_CARD_KEY) ?? window.localStorage.getItem(LEGACY_ACTIVE_KEY);
  } catch {
    return null;
  }
}

export async function initApp(): Promise<void> {
  applyTheme();
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTheme);
  effect(() => {
    void settings.value.theme;
    applyTheme();
  });

  await importLegacyIfNeeded();
  cardList.value = await db.listCards();

  const lastId = readActiveId();
  const target = (lastId && cardList.value.find((c) => c.id === lastId)?.id) ?? cardList.value[0]?.id ?? null;
  if (target) await openCard(target);
  else await newCard();

  if (legacyImportCount.value > 0) {
    const n = legacyImportCount.value;
    showToast(`Imported ${n} card${n === 1 ? '' : 's'} from the original Cue.`);
  }

  // A failed run is attached to the next prompt by default, so "why?" just works.
  effect(() => {
    if (runFailed.value) attach.value = { ...attach.peek(), output: true };
  });
  // Every finished run is a moment where the next step changes.
  let seenRuns = runCompletions.peek();
  effect(() => {
    const count = runCompletions.value;
    if (count !== seenRuns) {
      seenRuns = count;
      scheduleSuggestions();
    }
  });

  keepPreviewLive();

  const flush = () => persistActive.flush();
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function emptyCard(language = 'python'): Card {
  const now = Date.now();
  return {
    id: newId(),
    title: DEFAULT_TITLE,
    createdAt: now,
    updatedAt: now,
    messages: [],
    code: '',
    language,
    spec: '',
    specSync: null,
    review: null,
  };
}

function activate(card: Card): void {
  persistActive.flush();
  suggestionController?.abort();
  // Output belongs to the card that ran it; carried over, it would be shown and attached as this card's.
  if (activeCard.peek()?.id !== card.id) {
    stopRun();
    resetOutput();
  }
  batch(() => {
    activeCard.value = card;
    activeCode.value = card.code;
    activeSpec.value = card.spec;
    review.value = card.review ? restoredReview(card) : null;
    attach.value = { code: true, spec: true, output: false };
    suggestions.value = card.suggestions?.length ? { cardId: card.id, items: card.suggestions, status: 'ready' } : null;
  });
  try {
    window.localStorage.setItem(ACTIVE_CARD_KEY, card.id);
    window.localStorage.removeItem(LEGACY_ACTIVE_KEY);
  } catch {
    // storage unavailable
  }
}

/** The review signal for a card whose pending proposal was restored from storage. Editors refine it on mount. */
function restoredReview(card: Card): NonNullable<typeof review.value> {
  const r = card.review!;
  return {
    proposalId: r.proposalId,
    streaming: false,
    code: card.code !== r.original ? { remaining: 1, newFile: !r.original.trim() } : null,
    spec: card.spec !== r.originalSpec ? { remaining: 1, newFile: !r.originalSpec.trim() } : null,
  };
}

export async function newCard(): Promise<void> {
  const card = emptyCard(activeCard.value?.language ?? 'python');
  await db.putCard(card);
  updateList(card);
  activate(card);
  updateUi({ workspaceTab: 'code', ...(narrow() ? { sidebarOpen: false, mobileTab: 'prompt' as const } : {}) });
}

export async function openCard(id: string): Promise<void> {
  if (activeCard.value?.id === id) return;
  const card = await db.getCard(id);
  if (!card) {
    showToast('That card no longer exists.', 'error');
    cardList.value = await db.listCards();
    return;
  }
  activate(card);
  if (narrow()) updateUi({ sidebarOpen: false });
}

export async function deleteCard(id: string): Promise<void> {
  if (streaming.value?.cardId === id) stopStreaming();
  await db.deleteCard(id);
  cardList.value = cardList.value.filter((c) => c.id !== id);
  if (activeCard.value?.id === id) {
    persistActive.cancel();
    activeCard.value = null;
    const next = cardList.value[0];
    if (next) await openCard(next.id);
    else await newCard();
  }
}

export function renameCard(title: string): void {
  const clean = title.trim();
  if (!clean) return;
  mutateActive((card) => ({ ...card, title: clean }));
}

export function setLanguage(language: string): void {
  const id = normalizeLanguage(language);
  mutateActive((card) => ({ ...card, language: id }));
  void editorBridge.current?.setLanguage(id);
}

/** Called by the code editor on every user edit. Cheap: only the code signal and the debounced save are touched. */
export function onEditorChange(doc: string): void {
  if (activeCode.value === doc) return;
  activeCode.value = doc;
  persistActive();
}

/** Called by the spec editor on every user edit. */
export function onSpecChange(doc: string): void {
  if (activeSpec.value === doc) return;
  activeSpec.value = doc;
  persistActive();
}

/** The user says the spec and the code agree as they are. */
export function markSpecInStep(): void {
  mutateActive((card) => ({ ...card, specSync: syncOf(card.code, card.spec) }));
}

export function toggleAttachment(kind: 'code' | 'spec' | 'output'): void {
  attach.value = { ...attach.value, [kind]: !attach.value[kind] };
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function updateSettings(patch: Partial<Settings>): void {
  settings.value = { ...settings.value, ...patch, models: { ...settings.value.models, ...patch.models } };
  saveSettings(settings.value);
  if (patch.suggestions === false) suggestions.value = null;
}

export function setApiKey(provider: ProviderId, key: string): void {
  apiKeys.value = { ...apiKeys.value, [provider]: key.trim() };
  saveKeys(apiKeys.value, settings.value.keyStorage);
}

export function setKeyStorage(storage: Settings['keyStorage']): void {
  updateSettings({ keyStorage: storage });
  saveKeys(apiKeys.value, storage);
}

export function setModel(provider: ProviderId, model: string): void {
  updateSettings({ models: { [provider]: model.trim() } });
}

export async function refreshModels(provider: ProviderId, quiet = false): Promise<void> {
  const meta = providerMeta(provider);
  const key = apiKeys.value[provider] ?? '';
  if (meta.needsKey && !key && meta.id !== 'openrouter') {
    if (!quiet) showToast(`Add a ${meta.label} API key first.`, 'error');
    return;
  }
  modelListStatus.value = 'loading';
  try {
    const list = await (await createProvider(provider, key, currentModel.value)).listModels();
    modelOptions.value = { ...modelOptions.value, [provider]: list };
    modelListStatus.value = 'idle';
    if (!quiet) showToast(`Loaded ${list.length} models from ${meta.label}.`);
  } catch (error) {
    modelListStatus.value = 'error';
    if (!quiet) showToast(error instanceof Error ? error.message : String(error), 'error');
  }
}

async function resolveProvider(): Promise<Provider | null> {
  const id = settings.value.provider;
  const meta = providerMeta(id);
  const key = apiKeys.value[id] ?? '';
  if (meta.needsKey && !key.trim()) return null;
  return createProvider(id, key, currentModel.value);
}

// ---------------------------------------------------------------------------
// Messaging
// ---------------------------------------------------------------------------

function updateMessage(cardId: string, messageId: string, fn: (m: CardMessage) => CardMessage): Promise<void> {
  return mutateCard(cardId, (card) => ({ ...card, messages: card.messages.map((m) => (m.id === messageId ? fn(m) : m)) }));
}

function appendPart(cardId: string, messageId: string, part: MessagePart): Promise<void> {
  return updateMessage(cardId, messageId, (m) => ({ ...m, parts: [...m.parts, part] }));
}

/** Append streamed text to the last part of the same kind, or start a new part. */
function appendStreamed(cardId: string, messageId: string, kind: 'text' | 'reasoning', delta: string): Promise<void> {
  return updateMessage(cardId, messageId, (m) => {
    const last = m.parts[m.parts.length - 1];
    if (last && last.type === kind) return { ...m, parts: [...m.parts.slice(0, -1), { type: kind, text: last.text + delta }] };
    return { ...m, parts: [...m.parts, { type: kind, text: delta }] };
  });
}

function setProposalStatus(card: Card, proposalId: string | null, status: CodeProposal['status']): Card {
  if (!proposalId) return card;
  return {
    ...card,
    messages: card.messages.map((m) => ({
      ...m,
      parts: m.parts.map((p) => (p.type === 'proposal' && p.proposal.id === proposalId ? { ...p, proposal: { ...p.proposal, status } } : p)),
    })),
  };
}

function supersedePending(card: Card): Card {
  return {
    ...card,
    messages: card.messages.map((m) => ({
      ...m,
      parts: m.parts.map((p) =>
        p.type === 'proposal' && p.proposal.status === 'pending' ? { ...p, proposal: { ...p.proposal, status: 'superseded' } } : p,
      ),
    })),
  };
}

async function handleProposal(cardId: string, messageId: string, id: string, input: unknown): Promise<void> {
  const parsed = parseProposedChanges(input);
  if (!parsed) {
    await appendPart(cardId, messageId, { type: 'error', text: 'The model sent a proposal with neither code nor a spec in it.' });
    return;
  }
  const isActive = activeCard.value?.id === cardId;
  await mutateCard(cardId, (card) => {
    const language = parsed.code !== undefined && parsed.language ? normalizeLanguage(parsed.language) : card.language;
    const proposal: CodeProposal = {
      id,
      summary: parsed.summary,
      status: 'pending',
      ...(parsed.code !== undefined ? { code: parsed.code, language } : {}),
      ...(parsed.spec !== undefined ? { spec: parsed.spec } : {}),
    };
    // A proposal made while another is pending replaces it, and is compared with what was there before either.
    const original = card.review?.original ?? card.code;
    const originalSpec = card.review?.originalSpec ?? card.spec;
    const withPart = supersedePending(card);
    return {
      ...withPart,
      language,
      code: parsed.code ?? card.code,
      spec: parsed.spec ?? card.spec,
      review: { proposalId: id, original, originalSpec },
      messages: withPart.messages.map((m) => (m.id === messageId ? { ...m, parts: [...m.parts, { type: 'proposal', proposal }] } : m)),
    };
  });
  if (!isActive) return;
  const card = activeCard.value;
  if (!card?.review) return;
  batch(() => {
    activeCode.value = card.code;
    activeSpec.value = card.spec;
  });
  showReviewInEditors(card);
  persistNow();
  if (parsed.code === undefined) updateUi({ workspaceTab: 'spec' });
  else updateUi({ workspaceTab: 'code' });
  if (narrow()) updateUi({ mobileTab: 'code' });
}

/** Put the active card's pending review into both editors (or plain contents where nothing changed). */
function showReviewInEditors(card: Card): void {
  const code = editorBridge.current;
  const spec = specBridge.current;
  const r = card.review;
  if (!r) return;
  review.value = { proposalId: r.proposalId, streaming: false, code: null, spec: null };
  if (code) {
    if (card.code !== r.original) code.startReview(r.original, card.code);
    else if (code.isReviewing() || code.getDoc() !== card.code) code.setDoc(card.code);
    void code.setLanguage(card.language);
  }
  if (spec) {
    if (card.spec !== r.originalSpec) spec.startReview(r.originalSpec, card.spec);
    else if (spec.isReviewing() || spec.getDoc() !== card.spec) spec.setDoc(card.spec);
  }
  if (!code || !spec) review.value = restoredReview(card);
}

export interface SendOptions {
  action?: string;
  /** Attach the latest run output even if the user turned it off (the "Fix with AI" button). */
  withOutput?: boolean;
}

function buildContext(card: Card, withOutput: boolean): PromptContext | undefined {
  const a = attach.value;
  const context: PromptContext = {};
  if (a.code) context.code = { language: card.language, code: activeCode.value };
  if (a.spec) context.spec = { text: activeSpec.value, state: specState({ code: activeCode.value, spec: activeSpec.value, specSync: card.specSync }) };
  const output = outputText();
  if ((a.output || withOutput) && output.trim()) context.output = { text: output, failed: runFailed.value };
  return Object.keys(context).length ? context : undefined;
}

/** Returns false when nothing was sent (no provider, a reply still streaming), so callers can keep the draft. */
export async function sendMessage(text: string, options: SendOptions = {}): Promise<boolean> {
  const card = snapshotActive();
  const content = text.trim();
  if (!card || !content) return false;
  if (streaming.value) {
    showToast('Wait for the current reply to finish, or stop it first.');
    return false;
  }
  const provider = await resolveProvider();
  if (!provider) {
    updateUi({ settingsOpen: true });
    showToast('Pick a provider in Settings: add a key, or try the demo assistant.');
    return false;
  }

  suggestionController?.abort();
  suggestions.value = null;

  const userMessage: CardMessage = { id: newId(), role: 'user', parts: [{ type: 'text', text: content }], createdAt: Date.now() };
  if (options.action) userMessage.action = options.action;
  const context = buildContext(card, Boolean(options.withOutput));
  if (context) userMessage.context = context;
  userMessage.sent = buildSentPrompt(card.messages, content, context);
  // Output describes one run; once sent, it should not ride along again.
  if (attach.value.output) attach.value = { ...attach.value, output: false };
  const assistant: CardMessage = { id: newId(), role: 'assistant', parts: [], createdAt: Date.now() };
  mutateActive(({ suggestions: _stale, ...c }) => ({ ...c, messages: [...c.messages, userMessage, assistant] }));

  await runAssistantTurn(card.id, assistant.id, provider);
  return true;
}

async function runAssistantTurn(cardId: string, assistantId: string, provider: Provider): Promise<void> {
  const card = activeCard.value;
  if (!card || card.id !== cardId) return;
  const controller = new AbortController();
  streaming.value = { cardId, messageId: assistantId, controller, startedAt: Date.now(), tool: null };
  const system = buildSystemPrompt(settings.value.systemPrompt);
  const history = card.messages.filter((m) => m.id !== assistantId);

  const pending = { text: '', reasoning: '' };
  const flush = async () => {
    if (pending.reasoning) {
      const delta = pending.reasoning;
      pending.reasoning = '';
      await appendStreamed(cardId, assistantId, 'reasoning', delta);
    }
    if (pending.text) {
      const delta = pending.text;
      pending.text = '';
      await appendStreamed(cardId, assistantId, 'text', delta);
    }
  };
  const timer = setInterval(() => void flush(), 60);

  // Live preview: while the model writes a proposal, the code and spec appear in their editors as they arrive.
  const preview = { code: null as string | null, spec: null as string | null, language: null as string | null };
  const started = { code: false, spec: false };
  let previewTimer: ReturnType<typeof setTimeout> | null = null;
  const applyPreview = () => {
    previewTimer = null;
    const current = activeCard.value;
    if (!current || current.id !== cardId) return;
    const original = current.review?.original ?? activeCode.value;
    const originalSpec = current.review?.originalSpec ?? activeSpec.value;
    if (preview.code !== null && editorBridge.current) {
      if (!started.code) {
        started.code = true;
        updateUi({ workspaceTab: 'code', ...(narrow() ? { mobileTab: 'code' as const } : {}) });
      }
      if (preview.language) {
        void editorBridge.current.setLanguage(normalizeLanguage(preview.language));
        preview.language = null;
      }
      editorBridge.current.streamReview(original, preview.code);
    }
    if (preview.spec !== null && specBridge.current) {
      if (!started.spec) {
        started.spec = true;
        // Only a proposal that is all spec takes over the view; otherwise the spec tab shows a marker.
        if (!started.code) updateUi({ workspaceTab: 'spec', ...(narrow() ? { mobileTab: 'code' as const } : {}) });
      }
      specBridge.current.streamReview(originalSpec, preview.spec);
    }
  };
  const stopPreview = () => {
    if (previewTimer) clearTimeout(previewTimer);
    previewTimer = null;
    preview.code = preview.spec = preview.language = null;
    const any = started.code || started.spec;
    started.code = started.spec = false;
    return any;
  };

  let completed = false;
  let native: NativeTurn | undefined;
  let usage: Usage | undefined;
  try {
    for await (const event of provider.stream({
      model: currentModel.value,
      system,
      messages: history,
      tools: [proposeChangesTool],
      effort: settings.value.effort,
      sessionId: cardId,
      signal: controller.signal,
    })) {
      if (event.type === 'text') {
        pending.text += event.delta;
      } else if (event.type === 'reasoning') {
        pending.reasoning += event.delta;
      } else if (event.type === 'tool-progress') {
        if (event.name !== PROPOSE_CHANGES) continue;
        const fields = readPartialStringFields(event.partialJson);
        if (fields.code) {
          preview.code = fields.code.value;
          if (!started.code && fields.language?.complete) preview.language = fields.language.value;
        }
        if (fields.spec) preview.spec = fields.spec.value;
        if (fields.code || fields.spec) previewTimer ??= setTimeout(applyPreview, 80);
        const target = fields.spec && !fields.spec.complete ? 'spec' : 'code';
        const lines = countLines((target === 'spec' ? fields.spec?.value : fields.code?.value) ?? '');
        if (streaming.value) streaming.value = { ...streaming.value, tool: { target, lines } };
      } else if (event.type === 'tool-call') {
        await flush();
        stopPreview();
        if (streaming.value) streaming.value = { ...streaming.value, tool: null };
        if (event.name === PROPOSE_CHANGES) await handleProposal(cardId, assistantId, event.id, event.input);
        else await appendPart(cardId, assistantId, { type: 'error', text: `The model called a tool Cue does not have: ${event.name}.` });
      } else if (event.type === 'native') {
        native = event.native;
      } else if (event.type === 'usage') {
        usage = event.usage;
      } else if (event.type === 'done') {
        await flush();
        completed = true;
      }
    }
  } catch (error) {
    await flush();
    const text = isAbort(error) ? 'Stopped.' : error instanceof Error ? error.message : String(error);
    await appendPart(cardId, assistantId, { type: 'error', text });
  } finally {
    clearInterval(timer);
    await flush();
    // A proposal cut off mid-stream never becomes a proposal: put the editors back as they were.
    if (stopPreview() && activeCard.value?.id === cardId) syncEditorsToCard();
    streaming.value = null;
  }
  if (native || usage) {
    await updateMessage(cardId, assistantId, (m) => ({ ...m, ...(native && completed ? { native } : {}), ...(usage ? { usage } : {}) }));
  }
  if (activeCard.value?.id === cardId) persistNow();

  const finished = activeCard.value?.id === cardId ? activeCard.value : await db.getCard(cardId);
  if (finished && finished.title === DEFAULT_TITLE && finished.messages.length >= 2) void generateTitle(finished, provider);
  if (completed) scheduleSuggestions();
}

async function generateTitle(card: Card, provider: Provider): Promise<void> {
  try {
    const raw = await provider.complete({ system: TITLE_SYSTEM, prompt: titlePrompt(card), maxTokens: 60 });
    const title = raw
      .split('\n')[0]
      ?.replace(/^["'`*#\s]+|["'`*.\s]+$/g, '')
      .slice(0, 80)
      .trim();
    if (!title) return;
    await mutateCard(card.id, (c) => (c.title === DEFAULT_TITLE ? { ...c, title } : c));
    if (activeCard.value?.id === card.id) persistNow();
  } catch (error) {
    // A missing title is not worth interrupting the user for (they can rename the card), but it should be traceable.
    if (!isAbort(error)) console.warn('Cue: could not name the card.', error);
  }
}

export function stopStreaming(): void {
  streaming.value?.controller.abort();
}

/** Edit an earlier prompt: drop everything after it and ask again. */
export async function editUserMessage(messageId: string, text: string): Promise<void> {
  const card = activeCard.value;
  if (!card || streaming.value) return;
  const index = card.messages.findIndex((m) => m.id === messageId);
  if (index < 0) return;
  mutateActive((c) => ({ ...c, messages: c.messages.slice(0, index) }));
  await sendMessage(text, { action: card.messages[index]?.action });
}

export async function retryLastTurn(): Promise<void> {
  const card = activeCard.value;
  if (!card || streaming.value) return;
  const last = card.messages[card.messages.length - 1];
  if (!last || last.role !== 'assistant') return;
  const provider = await resolveProvider();
  if (!provider) {
    updateUi({ settingsOpen: true });
    return;
  }
  mutateActive((c) => ({ ...c, messages: c.messages.map((m) => (m.id === last.id ? { id: m.id, role: m.role, createdAt: m.createdAt, parts: [] } : m)) }));
  await runAssistantTurn(card.id, last.id, provider);
}

// ---------------------------------------------------------------------------
// Proposals and review
// ---------------------------------------------------------------------------

/** Set while a review is being closed, so the editors' own progress reports do not re-enter it. */
let finishing = false;

function finishReview(keep: 'current' | 'original'): void {
  const card = snapshotActive();
  const r = card?.review;
  if (!card || !r || finishing) return;
  finishing = true;
  let finalCode: string;
  let finalSpec: string;
  try {
    finalCode = editorBridge.current?.endReview(keep) ?? (keep === 'original' ? r.original : card.code);
    finalSpec = specBridge.current?.endReview(keep) ?? (keep === 'original' ? r.originalSpec : card.spec);
  } finally {
    finishing = false;
  }
  const changed = finalCode !== r.original || finalSpec !== r.originalSpec;
  const status = keep === 'current' && changed ? 'accepted' : 'rejected';
  const wasInStep = specState({ code: r.original, spec: r.originalSpec, specSync: card.specSync }) === 'in-step';
  // The spec agrees with the result when the proposal wrote it, or when it agreed before and the model left it alone.
  const specSync =
    status === 'accepted' && finalSpec.trim() && (finalSpec !== r.originalSpec || wasInStep) ? syncOf(finalCode, finalSpec) : card.specSync;
  batch(() => {
    activeCode.value = finalCode;
    activeSpec.value = finalSpec;
    review.value = null;
  });
  mutateActive((c) => ({ ...setProposalStatus(c, r.proposalId, status), review: null, code: finalCode, spec: finalSpec, specSync }));
  scheduleSuggestions();
}

export function acceptReview(): void {
  finishReview('current');
  (ui.value.workspaceTab === 'spec' ? specBridge : editorBridge).current?.focus();
}

export function rejectReview(): void {
  finishReview('original');
  (ui.value.workspaceTab === 'spec' ? specBridge : editorBridge).current?.focus();
}

function docReview(state: ReviewState): DocReview | null {
  return state.reviewing ? { remaining: state.remaining, newFile: state.newFile } : null;
}

const total = (r: { code: DocReview | null; spec: DocReview | null }) => (r.code?.remaining ?? 0) + (r.spec?.remaining ?? 0);

/**
 * The editors report review progress. When the user has decided every change by hand in both documents,
 * the review closes as if they had pressed the button: accepted if anything was kept, rejected otherwise.
 */
function onReviewChange(doc: 'code' | 'spec', state: ReviewState): void {
  if (finishing) return;
  const previous = review.value;
  const pending = activeCard.value?.review;
  if (!previous && !state.reviewing) return;
  const next = {
    proposalId: state.streaming ? null : (pending?.proposalId ?? previous?.proposalId ?? null),
    streaming: state.streaming,
    code: doc === 'code' ? docReview(state) : (previous?.code ?? null),
    spec: doc === 'spec' ? docReview(state) : (previous?.spec ?? null),
  };
  if (!next.code && !next.spec) {
    if (!pending) review.value = null;
    return;
  }
  // Only the user's own per-change decisions close a review; Cue's own editor updates never do.
  const decidedLast = state.byUser && previous !== null && total(previous) > 0 && total(next) === 0;
  const anyNewFile = Boolean(next.code?.newFile || next.spec?.newFile);
  if (pending && decidedLast && !state.streaming && !anyNewFile) {
    finishReview('current');
    return;
  }
  review.value = next;
}

export const onCodeReviewChange = (state: ReviewState) => onReviewChange('code', state);
export const onSpecReviewChange = (state: ReviewState) => onReviewChange('spec', state);

/** Make both editors show exactly what the active card holds (after a streaming preview was abandoned). */
function syncEditorsToCard(): void {
  const card = snapshotActive();
  if (!card) return;
  editorBridge.current?.cancelStream(card.review ? card.review.original : card.code);
  specBridge.current?.cancelStream(card.review ? card.review.originalSpec : card.spec);
  if (card.review) showReviewInEditors(card);
  else review.value = null;
  void editorBridge.current?.setLanguage(card.language);
}

/** Put a snippet from the conversation into the editor through the same review as a model proposal. */
export function reviewSnippet(code: string, language: string): void {
  const card = snapshotActive();
  if (!card) return;
  const original = card.review?.original ?? card.code;
  const originalSpec = card.review?.originalSpec ?? card.spec;
  const lang = language ? normalizeLanguage(language) : card.language;
  mutateActive((c) => ({
    ...setProposalStatus(c, c.review?.proposalId ?? null, 'superseded'),
    language: lang,
    code,
    spec: originalSpec,
    review: { proposalId: null, original, originalSpec },
  }));
  batch(() => {
    activeCode.value = code;
    activeSpec.value = originalSpec;
  });
  const updated = activeCard.value;
  if (updated) showReviewInEditors(updated);
  updateUi({ workspaceTab: 'code', ...(narrow() ? { mobileTab: 'code' as const } : {}) });
}

// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------

let suggestionController: AbortController | null = null;
let suggestionTimer: ReturnType<typeof setTimeout> | null = null;

/** Ask for fresh suggestions shortly, so a burst of events (accept, then run) makes one request. */
function scheduleSuggestions(): void {
  if (suggestionTimer) clearTimeout(suggestionTimer);
  suggestionTimer = setTimeout(() => void refreshSuggestions(), 300);
}

function suggestionState(card: Card): SuggestionState {
  const lastUser = [...card.messages].reverse().find((m) => m.role === 'user');
  const lastAssistant = [...card.messages].reverse().find((m) => m.role === 'assistant');
  const textOf = (m?: CardMessage) => m?.parts.filter((p) => p.type === 'text').map((p) => p.text).join('\n') ?? '';
  const lastProposal = lastAssistant?.parts.filter((p) => p.type === 'proposal').pop();
  const status = lastProposal?.type === 'proposal' ? lastProposal.proposal.status : 'none';
  const output = outputText();
  return {
    language: card.language,
    lastPrompt: textOf(lastUser),
    lastReply: textOf(lastAssistant),
    proposal: status === 'superseded' ? 'none' : status,
    specState: specState({ code: activeCode.value, spec: activeSpec.value, specSync: card.specSync }),
    hasCode: activeCode.value.trim() !== '',
    codeExcerpt: activeCode.value.split('\n').slice(0, 120).join('\n'),
    run: output.trim() ? (runFailed.value ? 'failed' : 'ok') : 'none',
    runOutput: output,
  };
}

export async function refreshSuggestions(): Promise<void> {
  const card = snapshotActive();
  if (!card || !settings.value.suggestions || streaming.value) return;
  if (card.messages.length === 0 && !activeCode.value.trim()) return;
  const provider = await resolveProvider();
  if (!provider) return;
  suggestionController?.abort();
  const controller = new AbortController();
  suggestionController = controller;
  suggestions.value = { cardId: card.id, items: suggestions.value?.cardId === card.id ? suggestions.value.items : [], status: 'loading' };
  try {
    const result = await provider.structured({
      name: 'suggestions',
      schema: SUGGESTIONS_SCHEMA as unknown as Record<string, unknown>,
      system: SUGGESTIONS_SYSTEM,
      prompt: suggestionsPrompt(suggestionState(card)),
      maxTokens: 400,
      signal: controller.signal,
    });
    if (controller.signal.aborted) return;
    const items = cleanSuggestions(result);
    await keepSuggestions(card.id, items);
    if (activeCard.value?.id === card.id) suggestions.value = { cardId: card.id, items, status: 'ready' };
  } catch (error) {
    if (controller.signal.aborted || isAbort(error)) return;
    console.warn('Cue: could not get suggestions.', error);
    if (activeCard.value?.id === card.id) suggestions.value = null;
  }
}

/** Saved without touching updatedAt: new suggestions are not an edit, so the card keeps its place in the list. */
async function keepSuggestions(cardId: string, items: string[]): Promise<void> {
  const active = activeCard.value;
  if (active?.id === cardId) {
    activeCard.value = { ...active, suggestions: items };
    persistActive();
    return;
  }
  const stored = await db.getCard(cardId);
  if (stored) await db.putCard({ ...stored, suggestions: items });
}

export async function sendSuggestion(text: string): Promise<void> {
  await sendMessage(text, { action: 'suggestion' });
}

// ---------------------------------------------------------------------------
// Running code and derived actions
// ---------------------------------------------------------------------------

/**
 * Once an HTML preview is showing (in the output panel or its own window), it follows the code: every edit,
 * accepted proposal or undo re-renders it after a short pause. While a reply streams, the code is half
 * written, so the preview waits for the end and then shows the proposal as it would look.
 */
function keepPreviewLive(): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  effect(() => {
    previewTitle.value = activeCard.value?.title ?? '';
  });
  effect(() => {
    const code = activeCode.value;
    const language = activeCard.value?.language;
    const showing = (previewDoc.value !== null && previewKind.value === 'html') || previewUndocked.value;
    if (timer) clearTimeout(timer);
    if (language !== 'html' || !showing || streaming.value !== null || code === previewSource.peek()) return;
    timer = setTimeout(() => void runCode('html', activeCode.peek(), { live: true }), 350);
  });
}

export async function runActiveCode(): Promise<void> {
  const card = activeCard.value;
  if (!card) return;
  updateUi({ outputOpen: true, ...(narrow() ? { mobileTab: 'output' as const } : {}) });
  await runCode(card.language, activeCode.value);
}

export function stopActiveRun(): void {
  stopRun();
}

export async function fixWithAi(): Promise<void> {
  if (!runFailed.value) return;
  if (narrow()) updateUi({ mobileTab: 'prompt' });
  await sendMessage(PROMPTS.fixRun, { action: 'fix', withOutput: true });
}

export async function inlineEdit(instruction: string): Promise<void> {
  const selection = editorBridge.current?.getSelection();
  updateUi({ inlineEditOpen: false });
  if (!selection) {
    await sendMessage(instruction, { action: 'inline-edit' });
    return;
  }
  await sendMessage(editSelectionMessage(instruction, selection.text, selection.fromLine, selection.toLine), { action: 'inline-edit' });
}

/** The spec tab's buttons: each one is an ordinary prompt, so it shows in the conversation. */
const SPEC_PROMPTS = { write: PROMPTS.writeSpec, update: PROMPTS.updateSpec, check: PROMPTS.checkSpec, follow: PROMPTS.followSpec };

export async function specAction(kind: keyof typeof SPEC_PROMPTS): Promise<void> {
  const prompt = SPEC_PROMPTS[kind];
  if (narrow()) updateUi({ mobileTab: 'prompt' });
  await sendMessage(prompt, { action: `spec-${kind}` });
}

// ---------------------------------------------------------------------------
// Data management
// ---------------------------------------------------------------------------

export async function exportData(): Promise<Blob> {
  persistActive.flush();
  const cards = await db.exportCards();
  const { provider, models, systemPrompt, theme, effort, suggestions: suggest } = settings.value;
  const payload = {
    app: 'cue',
    version: 3,
    exportedAt: new Date().toISOString(),
    settings: { provider, models, systemPrompt, theme, effort, suggestions: suggest },
    cards,
  };
  return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
}

export async function importData(file: File): Promise<void> {
  const parsed = JSON.parse(await file.text()) as { cards?: unknown[]; chats?: unknown[]; settings?: Partial<Settings> };
  // Exports made before cards existed call them chats.
  const cards = parsed.cards ?? parsed.chats;
  if (!Array.isArray(cards)) throw new Error('This file does not contain Cue cards.');
  const count = await db.importCards(cards);
  if (parsed.settings) {
    const { provider, models, systemPrompt, theme, effort } = parsed.settings;
    updateSettings({
      ...(provider ? { provider } : {}),
      ...(models ? { models } : {}),
      ...(systemPrompt !== undefined ? { systemPrompt } : {}),
      ...(theme ? { theme } : {}),
      ...(effort ? { effort } : {}),
    });
  }
  cardList.value = await db.listCards();
  showToast(`Imported ${count} card${count === 1 ? '' : 's'}.`);
}

export async function eraseEverything(): Promise<void> {
  stopStreaming();
  persistActive.cancel();
  await db.clearCards();
  clearAllLocalData();
  window.location.reload();
}

function countLines(text: string): number {
  let lines = 1;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) lines++;
  return lines;
}
